import { RiskLabelCountAll } from '@api';
import { CircleLoader, Tooltip as CustomTooltip } from '@components/ui';
import { depaginate, useApi } from '@data-access';
import { riskProfileStore } from '@stores/riskProfile.store';
import { AppRoutes } from '@types';
import { observer } from 'mobx-react';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import { DashboardApiFilterParameters } from 'src/pages/dashboard';

import styles from './RiskMatrixDashboard.module.scss';

type ColorCategory = 'green' | 'amber' | 'red';

type CellData = {
  count: number;
  colorOrder: number;
  residualRiskLabel: string;
  useCases: RiskLabelCountAll['useCases'];
};

const getColorCategory = (colorOrder: number, max: number): ColorCategory => {
  if (max === 0 || colorOrder == null) return 'green';
  const ratio = colorOrder / max;
  if (ratio <= 0.33) return 'green';
  if (ratio <= 0.66) return 'amber';
  return 'red';
};

const colorCellClass: Record<ColorCategory, string> = {
  green: styles.cellGreen,
  amber: styles.cellAmber,
  red: styles.cellRed,
};

const extractDimensionLabels = (dimensionData): Record<string, string[]> => {
  const map: Record<string, string[]> = {};
  dimensionData.forEach(({ dimension, name }) => {
    if (!map[dimension.name]) map[dimension.name] = [];
    map[dimension.name].push(name);
  });
  return map;
};

const buildGridData = (
  matrixData: RiskLabelCountAll[],
  dimension1Name: string,
  dimension2Name: string,
  dimension1Labels: string[],
  dimension2Labels: string[],
): { grid: CellData[][]; maxColorOrder: number } => {
  const grid: CellData[][] = Array.from({ length: dimension2Labels.length }, () =>
    Array(dimension1Labels.length).fill(null),
  );
  let maxColorOrder = 0;

  matrixData?.forEach(({ count, labels, colorOrder, residualRiskLabel, useCases }) => {
    const dim1Label = labels.find(l => l.dimension?.name === dimension1Name)?.name;
    const dim2Label = labels.find(l => l.dimension?.name === dimension2Name)?.name;
    const dim1Index = dimension1Labels.indexOf(dim1Label);
    const dim2Index = dimension2Labels.indexOf(dim2Label);
    const numericOrder = Number(colorOrder);
    if (numericOrder > maxColorOrder) maxColorOrder = numericOrder;
    if (dim1Index !== -1 && dim2Index !== -1) {
      grid[dim2Index][dim1Index] = { count, colorOrder: numericOrder, residualRiskLabel, useCases };
    }
  });

  return { grid, maxColorOrder };
};

export const RiskMatrixDashboard = observer(({ appliedFilters }: { appliedFilters: DashboardApiFilterParameters }) => {
  const { riskProfileApi } = useApi();
  const [isLoading, setIsLoading] = useState(true);
  const [riskMatrixData, setRiskMatrixData] = useState<RiskLabelCountAll[]>();

  const { dimensionRiskLabels } = riskProfileStore;

  const fetchRiskMatrix = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedRiskProfiles = await depaginate(page => riskProfileApi.apiRiskProfilesList({ page }));
      if (fetchedRiskProfiles.length === 0) return;
      const results = await riskProfileApi.apiRiskProfileDimensionMatrixAllRetrieve(appliedFilters);
      setRiskMatrixData(results.riskLabelCounts);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [riskProfileApi, appliedFilters]);

  useEffect(() => {
    fetchRiskMatrix();
  }, [fetchRiskMatrix]);

  if (isLoading) {
    return <CircleLoader className={styles.loader} />;
  }

  if (!riskMatrixData?.length) return null;

  const dimensionLabels = extractDimensionLabels(dimensionRiskLabels);
  const [dimension1Name, dimension2Name] = Object.keys(dimensionLabels);
  const dimension1Labels: string[] = dimensionLabels[dimension1Name] ?? [];
  const dimension2Labels: string[] = [...(dimensionLabels[dimension2Name] ?? [])].reverse();

  const { grid, maxColorOrder } = buildGridData(
    riskMatrixData,
    dimension1Name,
    dimension2Name,
    dimension1Labels,
    dimension2Labels,
  );

  const summaryCounts: Record<ColorCategory, number> = { green: 0, amber: 0, red: 0 };
  grid.forEach(row =>
    row.forEach(cell => {
      if (cell?.count > 0) {
        summaryCounts[getColorCategory(cell.colorOrder, maxColorOrder)] += cell.count;
      }
    }),
  );

  return (
    <div className={styles.matrix}>
      <div className={styles.summaryPills}>
        {summaryCounts.green > 0 && (
          <span className={`${styles.pill} ${styles.pillGreen}`}>{summaryCounts.green} low</span>
        )}
        {summaryCounts.amber > 0 && (
          <span className={`${styles.pill} ${styles.pillAmber}`}>{summaryCounts.amber} medium</span>
        )}
        {summaryCounts.red > 0 && <span className={`${styles.pill} ${styles.pillRed}`}>{summaryCounts.red} high</span>}
      </div>

      <div
        className={styles.matrixGrid}
        style={{ gridTemplateColumns: `56px repeat(${dimension1Labels.length}, 1fr)` }}
      >
        <div className={styles.emptyCell} />
        {dimension1Labels.map(label => (
          <div
            key={label}
            className={styles.colLabel}
          >
            {label}
          </div>
        ))}

        {dimension2Labels.map((rowLabel, rowIdx) => (
          <React.Fragment key={rowLabel}>
            <div className={styles.rowLabel}>{rowLabel}</div>
            {dimension1Labels.map((colLabel, colIdx) => {
              const cell = grid[rowIdx]?.[colIdx];
              const cat = cell ? getColorCategory(cell.colorOrder, maxColorOrder) : 'green';
              return (
                <CustomTooltip
                  key={`${rowLabel}-${colLabel}`}
                  placement="right"
                  hideArrow
                  content={
                    <div className={styles.tooltipContent}>
                      <strong>Use Cases</strong>
                      <div className={styles.tooltipList}>
                        {cell?.useCases?.length > 0 ? (
                          cell.useCases.map(uc => (
                            <Link
                              key={uc.id}
                              className={styles.tooltipLink}
                              href={`${AppRoutes.useCaseDetails}/${uc.id}/`}
                            >
                              {uc.name}
                            </Link>
                          ))
                        ) : (
                          <span>No use cases</span>
                        )}
                      </div>
                    </div>
                  }
                  className={styles.tooltip}
                >
                  <div className={`${styles.cell} ${colorCellClass[cat]}`}>{cell?.count > 0 ? cell.count : '—'}</div>
                </CustomTooltip>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div
        className={styles.xAxisLabel}
        style={{ paddingLeft: 56 }}
      >
        {dimension1Name} →
      </div>

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotGreen}`} />
          Low
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotAmber}`} />
          Medium
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendDotRed}`} />
          High
        </div>
      </div>
    </div>
  );
});
